import React, { useEffect, useRef, useState } from "react";
import { Link, useParams, useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError, formatRelative } from "../lib/api";
import Layout from "../components/Layout";
import TranslationProgressBanner from "../components/TranslationProgressBanner";
import AuthImage from "../components/AuthImage";
import MediaPlayerModal from "../components/MediaPlayerModal";
import { useAuth } from "../contexts/AuthContext";
import { Upload, Image as ImageIcon, Play, Lock, Coins, Pencil, Trash2, Star } from "lucide-react";
import Trophy from "../components/Trophy";
import SetPriceButton, { BuyContentButton } from "../components/ContentPrice";
import WeeklyOptOutToggle from "../components/WeeklyOptOutToggle";
import AccessModeSelector, { packAccess } from "../components/AccessModeSelector";
import AgeGateGuard from "../components/AgeGateGuard";

const TIER_SIZE_MB = {
  image: { free: 12, vip: 20, creator: 30 },
  video: { free: 100, vip: 250, creator: 500 },
};

const formatDuration = (sec) => {
  if (!sec || sec <= 0) return "";
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
};

export default function AlbumView() {
  return (
    <AgeGateGuard>
      <AlbumViewInner />
    </AgeGateGuard>
  );
}

function AlbumViewInner() {
  const { albumId } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [consent, setConsent] = useState(false);
  const [caption, setCaption] = useState("");
  const [visibility, setVisibility] = useState("public");
  const [access, setAccess] = useState({ mode: "public" });
  const [requiresTier, setRequiresTier] = useState("free");
  const [tags, setTags] = useState("");
  const [categories, setCategories] = useState("");
  const [showLocation, setShowLocation] = useState(false);
  const [showAge, setShowAge] = useState(false);
  const [error, setError] = useState("");
  const [uploading, setUploading] = useState(false);
  const [watermark, setWatermark] = useState("");  // tom = bruk brukerens default
  const [activeMedia, setActiveMedia] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [trophyMap, setTrophyMap] = useState({});
  const fileRef = useRef(null);
  const thumbRef = useRef(null);

  // Rediger albuminfo-modal state
  const [editOpen, setEditOpen] = useState(false);
  const [editTitle, setEditTitle] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editNsfw, setEditNsfw] = useState(true);
  const [editSaving, setEditSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const nav = useNavigate();

  const openEdit = () => {
    if (!data?.album) return;
    setEditTitle(data.album.title || "");
    setEditDescription(data.album.description || "");
    setEditNsfw(!!data.album.is_nsfw);
    setEditOpen(true);
  };

  const saveAlbumInfo = async () => {
    setEditSaving(true);
    try {
      await api.patch(`/albums/${albumId}`, {
        title: editTitle.trim(),
        description: editDescription.trim(),
        is_nsfw: editNsfw,
      });
      await load();
      setEditOpen(false);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setEditSaving(false);
    }
  };

  const deleteAlbum = async () => {
    const ok = window.confirm("Er du sikker på at du vil slette albumet og ALT innholdet permanent? Dette kan ikke angres.");
    if (!ok) return;
    setDeleting(true);
    try {
      await api.delete(`/albums/${albumId}`);
      nav("/albums");
    } catch (e) {
      setError(formatApiError(e));
      setDeleting(false);
    }
  };

  const deleteImage = async (imageId, isVid, e) => {
    if (e) { e.stopPropagation(); e.preventDefault(); }
    const label = isVid ? "videoen" : "bildet";
    if (!window.confirm(`Slette ${label} permanent? Dette kan ikke angres.`)) return;
    try {
      const img = (data?.images || []).find((x) => x.id === imageId);
      if (img?.source_post_id) {
        const also = window.confirm("Fjerne også fra innlegget i feed?\n\nOK = ja, fjern fra innlegget · Avbryt = behold i innlegget");
        await api.delete(`/images/${imageId}/feed-aware`, { params: { remove_from_post: also } });
      } else {
        await api.delete(`/images/${imageId}`);
      }
      await load();
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const setAsCover = async (imageId, e) => {
    if (e) { e.stopPropagation(); e.preventDefault(); }
    try {
      await api.post(`/albums/${albumId}/cover`, { image_id: imageId });
      await load();
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const clearCover = async (e) => {
    if (e) { e.stopPropagation(); e.preventDefault(); }
    try {
      await api.post(`/albums/${albumId}/cover`, { image_id: null });
      await load();
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const load = () => api.get(`/albums/${albumId}`).then((r) => setData(r.data));
  useEffect(() => {
    load(); /* eslint-disable-next-line */
  }, [albumId]);

  // Åpne modal fra ?image=<id>-param når data er lastet (deep-link + refresh)
  useEffect(() => {
    const imgId = searchParams.get("image");
    if (!imgId || !data?.images?.length) return;
    if (activeMedia?.id === imgId) return;
    const match = data.images.find((i) => i.id === imgId && !i.locked);
    if (match) setActiveMedia(match);
  }, [data?.images, searchParams, activeMedia?.id]);

  // Trofé-batch når bilder er lastet
  useEffect(() => {
    const images = data?.images || [];
    if (images.length === 0) { setTrophyMap({}); return; }
    const imageIds = images.filter((i) => i.media_type !== "video").map((i) => i.id);
    const videoIds = images.filter((i) => i.media_type === "video").map((i) => i.id);
    const calls = [];
    if (imageIds.length) calls.push(api.post("/contests/content-trophies", { type: "image", ids: imageIds }));
    if (videoIds.length) calls.push(api.post("/contests/content-trophies", { type: "video", ids: videoIds }));
    Promise.all(calls).then((rs) => {
      const merged = {};
      rs.forEach((r) => Object.assign(merged, r.data?.results || {}));
      setTrophyMap(merged);
    }).catch(() => setTrophyMap({}));
  }, [data?.images]);

  const userTier = user && user !== false ? (user.is_creator ? "creator" : user.tier || "free") : "free";

  const [uploadProgress, setUploadProgress] = useState(null); // {current, total, currentName}

  const upload = async () => {
    setError("");
    if (!consent) return setError("Du må bekrefte samtykke før du kan laste opp.");
    const files = Array.from(fileRef.current?.files || []);
    if (files.length === 0) return setError("Velg minst én fil.");

    // Valider alle filer først
    const invalid = [];
    for (const file of files) {
      const isV = file.type.startsWith("video/");
      const isI = file.type.startsWith("image/");
      if (!isV && !isI) { invalid.push(`${file.name}: ikke bilde/video`); continue; }
      const kind = isV ? "video" : "image";
      const limitMb = TIER_SIZE_MB[kind][userTier];
      if (file.size > limitMb * 1024 * 1024) {
        invalid.push(`${file.name}: ${(file.size / 1024 / 1024).toFixed(1)} MB overstiger ${limitMb} MB`);
      }
    }
    if (invalid.length) {
      return setError("Kan ikke laste opp:\n• " + invalid.join("\n• "));
    }

    setUploading(true);
    const errors = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      setUploadProgress({ current: i + 1, total: files.length, currentName: file.name });
      const isVideo = file.type.startsWith("video/");
      try {
        if (isVideo) {
          // ---- Direct-to-R2 flyt (v116) ----
          const packed = packAccess(access);
          const initBody = {
            filename: file.name,
            content_type: file.type,
            size: file.size,
            consent: true,
            caption: caption,
            visibility: packed.visibility,
            categories: categories,
            tags: tags,
            show_location: !!showLocation,
            show_age: !!showAge,
            requires_tier: "free",
            price_tokens: Number(packed.price_tokens || 0),
            access_friends_free: !!packed.access_friends_free,
            access_vip_free: !!packed.access_vip_free,
          };
          const initRes = await api.post(`/albums/${albumId}/videos/init-upload`, initBody);
          const { upload_url, video_id } = initRes.data;

          // PUT direkte til R2 med XHR (for progress-callback senere om vi vil)
          await new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            xhr.open("PUT", upload_url, true);
            xhr.setRequestHeader("Content-Type", file.type);
            xhr.onload = () => {
              if (xhr.status >= 200 && xhr.status < 300) resolve();
              else reject(new Error(`R2 PUT feilet: ${xhr.status} ${xhr.statusText}`));
            };
            xhr.onerror = () => reject(new Error("Nettverksfeil under opplasting til R2"));
            xhr.send(file);
          });

          // Finalize: backend genererer thumbnail/duration mot presigned GET-URL
          await api.post(`/albums/${albumId}/videos/finalize`, { video_id });
        } else {
          // ---- Bilde-flyt (uendret multipart) ----
          // v220: direkte til R2 (samme grenser som feed: 200 MB / 5 GB skaper+selger)
          const init = await api.post(`/albums/${albumId}/images/init-upload`,
            { filename: file.name, content_type: file.type, size: file.size });
          await new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            xhr.open("PUT", init.data.upload_url, true);
            xhr.setRequestHeader("Content-Type", file.type);
            xhr.onload = () => (xhr.status < 300 ? resolve() : reject(new Error(`Opplasting feilet (${xhr.status})`)));
            xhr.onerror = () => reject(new Error("Nettverksfeil under opplasting"));
            xhr.send(file);
          });
          const packed = packAccess(access);
          const body = {
            upload_id: init.data.upload_id, consent: true, caption,
            visibility: packed.visibility, price_tokens: packed.price_tokens || 0,
            access_friends_free: !!packed.access_friends_free, access_vip_free: !!packed.access_vip_free,
            requires_tier: "free",
            tags: tags.split(",").map((x) => x.trim()).filter(Boolean),
            categories: categories.split(",").map((x) => x.trim()).filter(Boolean),
            show_location: showLocation, show_age: showAge, watermark: watermark || "",
          };
          try {
            await api.post(`/albums/${albumId}/images/finalize`, body);
          } catch (fe) {
            const d = fe?.response?.data?.detail;
            if (d?.code !== "watermark_too_large" || !window.confirm(`${d.message}\n\nLagre uten innbrent vannmerke?`)) throw fe;
            await api.post(`/albums/${albumId}/images/finalize`, { ...body, skip_watermark: true });
          }
        }
      } catch (e) {
        errors.push(`${file.name}: ${formatApiError(e)}`);
      }
    }
    setUploadProgress(null);
    setCaption("");
    setTags("");
    setCategories("");
    setConsent(false);
    if (fileRef.current) fileRef.current.value = "";
    if (thumbRef.current) thumbRef.current.value = "";
    if (errors.length) {
      setError(`Lastet opp ${files.length - errors.length} av ${files.length}. Feilet:\n• ${errors.join("\n• ")}`);
    }
    await load();
    setUploading(false);
  };

  if (!data)
    return (
      <Layout>
        <div className="font-meta text-[var(--cream-soft)]">Laster…</div>
      </Layout>
    );
  const { album, images } = data;
  const isOwner = user && user !== false && user.id === album.owner_id;

  return (
    <Layout>
      <TranslationProgressBanner />
      <div className="font-meta mb-4">
        <Link to="/albums" className="hover:text-[var(--cream)]">Album</Link>
        <span className="mx-2">/</span>
        <Link to={`/profile/${album.owner.username}`} className="hover:text-[var(--cream)]">
          {album.owner.username}
        </Link>
      </div>

      <div className="border-b border-white/10 pb-6 mb-10">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-2">
          <div className="flex items-center gap-3">
            <h1 className="font-fraunces italic text-4xl text-[var(--cream)] flex items-center gap-3" data-testid="album-title">{album.system_kind && <Star className="h-6 w-6 text-[var(--magenta)]" data-testid="system-album-badge" />}{album.title}</h1>
            {album.is_nsfw && <span className="font-meta text-[var(--magenta)]">NSFW</span>}
          </div>
          {isOwner && (
            <div className="flex items-center gap-2 shrink-0">
              <button
                type="button"
                onClick={openEdit}
                data-testid="album-edit-info-btn"
                className="inline-flex items-center gap-2 px-3 py-1.5 border border-white/15 hover:border-[var(--magenta)] text-[var(--cream)] text-xs font-meta tracking-[0.15em] transition-colors"
              >
                <Pencil className="h-3.5 w-3.5" /> Rediger info
              </button>
              {!album.system_kind && <button
                type="button"
                onClick={deleteAlbum}
                disabled={deleting}
                data-testid="album-delete-btn"
                className="inline-flex items-center gap-2 px-3 py-1.5 border border-white/15 hover:border-red-500 hover:text-red-400 text-[var(--cream-soft)] text-xs font-meta tracking-[0.15em] transition-colors disabled:opacity-50"
              >
                <Trash2 className="h-3.5 w-3.5" /> Slett album
              </button>}
            </div>
          )}
        </div>
        <p className="font-fraunces text-[var(--cream)]/80" data-testid="album-description">
          {album.description || (
            <span className="italic text-[var(--cream-soft)]">Ingen beskrivelse</span>
          )}
        </p>
        <div className="font-meta mt-3">
          Opprettet {formatRelative(album.created_at)} · {images.length} medier
        </div>
        {!isOwner && user && user !== false && album.owner?.is_creator && album.owner?.username && (
          <div className="mt-4" />
        )}
      </div>

      {/* Rediger albuminfo-modal */}
      {editOpen && isOwner && (
        <div
          className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 pt-24 sm:pt-4 items-start sm:items-center"
          onClick={() => !editSaving && setEditOpen(false)}
          data-testid="album-edit-modal"
        >
          <div
            className="w-full max-w-md bg-[var(--noir-elev)] border border-white/10 p-6 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="font-fraunces italic text-2xl text-[var(--cream)] mb-5">Rediger albuminfo</h2>
            <div className="space-y-4">
              <div>
                <label className="block font-meta mb-1.5">Tittel</label>
                <input
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  minLength={2}
                  maxLength={80}
                  className="input-editorial"
                  data-testid="album-edit-title"
                />
              </div>
              <div>
                <label className="block font-meta mb-1.5">Beskrivelse</label>
                <textarea
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  rows={4}
                  maxLength={2000}
                  className="input-editorial w-full p-3 text-sm"
                  data-testid="album-edit-description"
                />
              </div>
              <label className="flex items-center gap-2 text-sm text-[var(--cream)] font-manrope">
                <input
                  type="checkbox"
                  checked={editNsfw}
                  onChange={(e) => setEditNsfw(e.target.checked)}
                  data-testid="album-edit-nsfw"
                />
                NSFW-innhold
              </label>
            </div>
            <div className="flex gap-2 mt-6">
              <button
                type="button"
                onClick={() => setEditOpen(false)}
                disabled={editSaving}
                className="flex-1 py-2.5 border border-white/15 text-[var(--cream-soft)] font-meta tracking-[0.15em] text-xs hover:text-[var(--cream)] disabled:opacity-50"
              >
                Avbryt
              </button>
              <button
                type="button"
                onClick={saveAlbumInfo}
                disabled={editSaving || editTitle.trim().length < 2}
                data-testid="album-edit-save-btn"
                className="flex-1 py-2.5 bg-[var(--magenta)] text-white font-meta tracking-[0.15em] text-xs hover:opacity-90 disabled:opacity-40"
              >
                {editSaving ? "Lagrer…" : "Lagre"}
              </button>
            </div>
          </div>
        </div>
      )}

      {isOwner && album.is_leak_album && (
        <div
          className="border border-[var(--magenta)] bg-[var(--magenta)]/10 p-5 mb-10 flex items-start gap-3"
          data-testid="album-leak-notice"
        >
          <Lock className="h-5 w-5 text-[var(--magenta)] shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-meta text-xs tracking-[0.15em] text-[var(--magenta)] uppercase mb-1">
              Låst privat
            </div>
            <div className="font-manrope text-sm text-[var(--cream)] mb-3">
              Kun du kan se disse filene. Håndteres i Leak-sjekk.
            </div>
            <Link
              to="/leak-sjekk"
              className="inline-block btn-magenta"
              data-testid="album-leak-go-to-leak-sjekk"
            >
              Åpne Leak-sjekk
            </Link>
          </div>
        </div>
      )}

      {isOwner && album.system_kind && (
        <p className="font-manrope text-sm text-[var(--cream-soft)] mb-8" data-testid="system-album-info">Dette albumet fylles automatisk fra innleggene dine i feeden. Vil du laste opp uten å dele i feeden, bruk et eget album.</p>
      )}
      {isOwner && !album.is_leak_album && !album.system_kind && (
        <div className="editorial-card p-6 mb-10 space-y-5" data-testid="upload-section">
          <h3 className="font-fraunces italic text-2xl text-[var(--cream)]">Last opp bilde eller video</h3>

          <div>
            <div className="font-meta mb-2">Velg fil</div>
            <input
              type="file"
              ref={fileRef}
              accept="image/*,video/mp4,video/webm,video/quicktime,video/x-matroska"
              multiple
              className="block w-full text-sm text-[var(--cream)] file:mr-3 file:py-2 file:px-3 file:border-0 file:bg-white/10 file:text-[var(--cream)] file:hover:bg-white/15 file:rounded-sm font-manrope"
              data-testid="upload-file-input"
            />
            <div className="font-meta mt-2 text-[10px]">
              Bilde maks {TIER_SIZE_MB.image[userTier]} MB · Video maks {TIER_SIZE_MB.video[userTier]} MB
              {userTier === "free" && " · Oppgrader for større filer"}
              <br /><span className="text-[var(--magenta)]">Tips: Hold Ctrl/Cmd for å velge flere filer samtidig.</span>
            </div>
          </div>

          <div>
            <div className="font-meta mb-2">Egen forhåndsvisning for video (valgfritt)</div>
            <input
              type="file"
              ref={thumbRef}
              accept="image/*"
              className="block w-full text-sm text-[var(--cream)] file:mr-3 file:py-2 file:px-3 file:border-0 file:bg-white/10 file:text-[var(--cream)] file:hover:bg-white/15 file:rounded-sm font-manrope"
              data-testid="upload-thumbnail-input"
            />
            <div className="font-meta mt-2 text-[10px]">
              Lar du dette stå tomt, brukes første frame av videoen automatisk.
            </div>
          </div>

          <div>
            <div className="font-meta mb-2">Bildetekst (valgfritt)</div>
            <input
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              className="input-editorial"
              data-testid="upload-caption"
            />
          </div>

          <div className="mb-5">
            <AccessModeSelector value={access} onChange={setAccess} />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <div className="font-meta mb-2">
                Kategorier{" "}
                <span className="normal-case tracking-normal text-[10px] text-[var(--cream-faint)]">
                  (kommaseparert)
                </span>
              </div>
              <input
                value={categories}
                onChange={(e) => setCategories(e.target.value)}
                placeholder="selfie, par, outdoor"
                className="input-editorial"
              />
            </div>
            <div>
              <div className="font-meta mb-2">
                Tags{" "}
                <span className="normal-case tracking-normal text-[10px] text-[var(--cream-faint)]">
                  (kommaseparert, uten #)
                </span>
              </div>
              <input
                value={tags}
                onChange={(e) => setTags(e.target.value)}
                placeholder="oslo, blond, lingerie"
                className="input-editorial"
                data-testid="upload-tags"
              />
            </div>
          </div>

          {visibility === "public" && (
            <div className="flex flex-wrap gap-5 pt-2">
              <label className="flex items-center gap-2 text-sm text-[var(--cream)] font-manrope">
                <input
                  type="checkbox"
                  checked={showLocation}
                  onChange={(e) => setShowLocation(e.target.checked)}
                  className="h-4 w-4 accent-[var(--magenta)]"
                />
                Vis lokasjon
              </label>
              <label className="flex items-center gap-2 text-sm text-[var(--cream)] font-manrope">
                <input
                  type="checkbox"
                  checked={showAge}
                  onChange={(e) => setShowAge(e.target.checked)}
                  className="h-4 w-4 accent-[var(--magenta)]"
                />
                Vis alder
              </label>
            </div>
          )}

          <div className="border-t border-white/10 pt-4">
            <label className="font-meta text-[var(--cream-soft)] block mb-2">VANNMERKE FOR DETTE OPPLASTET</label>
            <select
              value={watermark}
              onChange={(e) => setWatermark(e.target.value)}
              className="input-editorial w-full max-w-md"
              data-testid="upload-watermark"
            >
              <option value="">Bruk standardvalg ({user?.watermark_default === "corner" ? "Diskret" : user?.watermark_default === "diagonal" ? "Stor diagonal" : "Av"})</option>
              <option value="off">Av – ingen vannmerke</option>
              <option value="corner">Diskret – nederst til høyre</option>
              <option value="diagonal">Stor diagonal – på tvers av bildet</option>
            </select>
            <div className="font-meta text-[10px] text-[var(--cream-faint)] mt-1.5">
              Standardvalg styres i <a href="/innstillinger/innhold" className="text-[var(--magenta)] underline-offset-4 hover:underline">Innstillinger → Innhold</a>
            </div>
            {/* Live forhåndsvisning av valgt vannmerke */}
            {(() => {
              const effective = watermark || user?.watermark_default || "off";
              if (effective === "off") return null;
              return (
                <div className="mt-3 max-w-md" data-testid="upload-watermark-preview">
                  <div className="font-meta text-[10px] text-[var(--cream-soft)] mb-1.5 uppercase tracking-[0.15em]">
                    Forhåndsvisning ({effective === "corner" ? "Diskret" : "Stor diagonal"})
                  </div>
                  <img
                    src={`${process.env.REACT_APP_BACKEND_URL}/api/me/watermark-preview?variant=${effective}`}
                    alt="Forhåndsvisning av vannmerke"
                    className="w-full border border-white/10"
                  />
                </div>
              );
            })()}
          </div>

          <label className="flex items-start gap-3 cursor-pointer text-sm text-[var(--cream)] font-manrope border-t border-white/10 pt-4">
            <input
              type="checkbox"
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              className="mt-0.5 h-4 w-4 accent-[var(--magenta)]"
              data-testid="upload-consent"
            />
            <span>
              Jeg bekrefter at alle på bildet/videoen er over 18 år, har gitt samtykke til at innholdet
              deles, og at det er lovlig.
            </span>
          </label>

          {error && (
            <div className="text-sm text-[var(--cream)] font-medium border-l-2 border-[var(--magenta)] pl-3 whitespace-pre-wrap" data-testid="upload-error">
              {error}
            </div>
          )}

          {uploadProgress && (
            <div className="border border-white/10 p-3 bg-black/40" data-testid="upload-progress">
              <div className="font-meta text-xs text-[var(--cream)] mb-2">
                Laster opp {uploadProgress.current} av {uploadProgress.total}: <span className="text-[var(--cream-soft)] normal-case tracking-normal">{uploadProgress.currentName}</span>
              </div>
              <div className="h-1.5 bg-white/10 overflow-hidden">
                <div
                  className="h-full bg-[var(--magenta)] transition-all"
                  style={{ width: `${(uploadProgress.current / uploadProgress.total) * 100}%` }}
                />
              </div>
            </div>
          )}

          <button
            onClick={upload}
            disabled={uploading}
            className="btn-magenta"
            data-testid="upload-submit"
          >
            <Upload className="h-3.5 w-3.5 inline mr-2" /> {uploading ? "Laster opp…" : "Last opp"}
          </button>
        </div>
      )}

      {images.length === 0 ? (
        <div className="text-center py-20">
          <ImageIcon className="h-10 w-10 text-[var(--cream-faint)] mx-auto mb-3" />
          <div className="font-fraunces italic text-2xl text-[var(--cream-soft)]">Tomt album</div>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="image-grid">
          {images.map((img) => {
            const isVideo = img.media_type === "video";
            const requires = img.requires_tier || "free";
            const lockReason = isOwner ? null : img.locked;
            const ownerLocked = !!lockReason;
            const isCover = album.cover_image_id === img.id;
            // Video kan ikke brukes som album-cover (bare bilder, da listen-API'et
            // peker på /api/files/{id} som thumbnail-source)
            const coverable = isOwner && img.media_type !== "video";

            return (
              <button
                key={img.id}
                onClick={() => {
                  if (ownerLocked) return;
                  setActiveMedia(img);
                  const next = new URLSearchParams(searchParams);
                  next.set("image", img.id);
                  setSearchParams(next, { replace: false });
                }}
                className={`group relative aspect-square overflow-hidden bg-[var(--noir-elev)] ring-1 ${isCover ? "ring-2 ring-[var(--magenta)]" : "ring-white/5 hover:ring-[var(--magenta)]/40"} text-left`}
                data-testid={`album-image-${img.id}`}
              >
                {ownerLocked ? (
                  <div className="w-full h-full bg-black" data-testid={`album-locked-${img.id}`} />
                ) : (
                  <AuthImage
                    imageId={img.id}
                    alt={img.caption || ""}
                    className={`w-full h-full object-cover ${img.nsfw_category === "erotisk" ? "erotic-blur-target" : ""}`}
                  />
                )}

                {isVideo && !ownerLocked && (
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <div className="h-12 w-12 rounded-full bg-black/60 ring-1 ring-white/20 flex items-center justify-center group-hover:bg-[var(--magenta)] transition-colors">
                      <Play className="h-5 w-5 text-white ml-0.5" />
                    </div>
                  </div>
                )}

                <div className="absolute top-2 left-2 flex gap-1.5">
                  {isCover && (
                    <span className="font-meta text-[9px] bg-[var(--magenta)] text-white px-1.5 py-0.5 inline-flex items-center gap-1" data-testid={`cover-badge-${img.id}`}>
                      <Star className="h-2.5 w-2.5 fill-white" /> COVER
                    </span>
                  )}
                  {img.visibility !== "public" && (
                    <span className="font-meta text-[9px] bg-black/70 px-1.5 py-0.5">
                      {img.visibility === "private"
                        ? "Privat"
                        : img.visibility === "vip"
                        ? "VIP"
                        : img.visibility === "friends"
                        ? "Venner"
                        : img.visibility}
                    </span>
                  )}
                  {requires !== "free" && (
                    <span className="font-meta text-[9px] bg-[var(--magenta)] text-black px-1.5 py-0.5">
                      {requires === "creator" ? "CREATOR" : "PLUSS"}
                    </span>
                  )}
                </div>

                {/* Slett-knapp per bilde/video (kun eier) */}
                {isOwner && (
                  <button
                    type="button"
                    onClick={(e) => deleteImage(img.id, isVideo, e)}
                    data-testid={`delete-media-${img.id}`}
                    title={isVideo ? "Slett videoen permanent" : "Slett bildet permanent"}
                    className="absolute top-2 left-2 z-20 px-1.5 py-0.5 text-[9px] font-meta bg-black/80 hover:bg-red-600 text-[var(--cream)] tracking-[0.15em] inline-flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <Trash2 className="h-2.5 w-2.5" /> Slett
                  </button>
                )}

                {/* Sett som cover-knapp (kun eier, kun bilder, ikke video) */}
                {coverable && (
                  isCover ? (
                    <button
                      type="button"
                      onClick={(e) => clearCover(e)}
                      data-testid={`unset-cover-${img.id}`}
                      title="Fjern som cover (tilbake til automatisk – nyeste først)"
                      className="absolute top-2 right-2 z-20 px-1.5 py-0.5 text-[9px] font-meta bg-black/80 hover:bg-black text-[var(--cream)] tracking-[0.15em] inline-flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <Star className="h-2.5 w-2.5 fill-[var(--magenta)] text-[var(--magenta)]" /> Fjern
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={(e) => setAsCover(img.id, e)}
                      data-testid={`set-cover-${img.id}`}
                      title="Bruk dette bildet som cover på album-cardet"
                      className="absolute top-2 right-2 z-20 px-1.5 py-0.5 text-[9px] font-meta bg-black/80 hover:bg-[var(--magenta)] text-[var(--cream)] tracking-[0.15em] inline-flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <Star className="h-2.5 w-2.5" /> Sett som cover
                    </button>
                  )
                )}

                {isVideo && (
                  <span className="absolute top-2 right-2 font-meta text-[9px] bg-black/70 px-1.5 py-0.5 text-[var(--cream)]">
                    {img.duration_seconds ? formatDuration(img.duration_seconds) : "VIDEO"}
                  </span>
                )}

                {trophyMap[img.id] && (
                  <span
                    className={`absolute z-10 ${isVideo ? "top-9" : "top-2"} right-2 pointer-events-auto`}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Trophy
                      place={trophyMap[img.id].place}
                      contestTitle={trophyMap[img.id].contest_title}
                      contestId={trophyMap[img.id].contest_id}
                      variant="corner"
                      size={16}
                    />
                  </span>
                )}

                {/* Pris-badge */}
                {img.price_tokens > 0 && (
                  <span className="absolute bottom-2 left-2 z-10 inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-meta bg-[var(--gold)]/90 text-black">
                    <Coins className="h-2.5 w-2.5" /> {img.price_tokens}
                  </span>
                )}
                {/* Eier kan sette pris (overlay) + opt-out ukens konkurranse */}
                {isOwner && (
                  <span className="absolute bottom-2 right-2 z-10 flex flex-col gap-1.5 items-end" onClick={(e) => e.stopPropagation()}>
                    {!album.system_kind && <SetPriceButton
                      contentType={img.media_type === "video" ? "video" : "image"}
                      contentId={img.id}
                      currentPrice={img.price_tokens || 0}
                      onSaved={() => load()}
                    />}
                    {img.media_type === "image" && (
                      <WeeklyOptOutToggle
                        imageId={img.id}
                        optedOut={!!img.opt_out_weekly_contest}
                        onChanged={() => load()}
                      />
                    )}
                  </span>
                )}

                {ownerLocked && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 p-2 text-center" data-testid={`album-lock-${lockReason}-${img.id}`}>
                    <Lock className="h-7 w-7 text-[var(--magenta)]" />
                    <span className="font-meta text-[10px] uppercase tracking-[0.15em] text-[var(--cream)]">
                      {lockReason === "paid" ? "Betalt innhold" : lockReason === "vip_video" ? "Film krever VIP" : lockReason === "vip" ? "Kun for VIP" : requires === "creator" ? "Krever Creator" : "Krever VIP"}
                    </span>
                    {lockReason === "paid" && user && user !== false && (
                      <span onClick={(e) => e.stopPropagation()}>
                        <BuyContentButton
                          contentType={isVideo ? "video" : "image"}
                          contentId={img.id}
                          price={img.price_tokens}
                          hasAccess={false}
                          sellerUsername={album.owner?.username}
                          onPurchased={() => load()}
                        />
                      </span>
                    )}
                  </div>
                )}

                {img.caption && (
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 to-transparent p-2">
                    <p className="font-manrope text-[11px] text-[var(--cream)] truncate">
                      {img.caption}
                    </p>
                  </div>
                )}
              </button>
            );
          })}
        </div>
      )}

      <MediaPlayerModal media={activeMedia} items={(data?.images || []).filter((i) => isOwner || !i.locked)} onClose={() => {
        setActiveMedia(null);
        if (searchParams.get("image")) {
          const next = new URLSearchParams(searchParams);
          next.delete("image");
          setSearchParams(next, { replace: true });
        }
      }} />

      {/* Album-kommentarer fjernet (Iter 47.5) – kommentarer hører til hvert
          enkelt bilde og vises i lightbox ved klikk på bildet. */}
    </Layout>
  );
}
